import React, { useCallback, useRef, useState } from "react";
import Popover from "./datepicker/Popover";
import RangePanel from "./datepicker/RangePanel";
import { rangePresets, rangeText, todayYmd } from "./datepicker/calendar.mjs";
import useIsMobile from "../hooks/useIsMobile";

/*
 * The scope control: 30d / 90d / 12 mo / Custom.
 *
 * Replaces v1's "Last N months" select, which measured the wrong thing. That
 * select set how many months of BARS each module drew; nothing on the page had
 * a window. Reports v2's numbers are cohort numbers, and a cohort is a span of
 * days, so the control has to name days.
 *
 * Why the default matters more than it looks: the 12-month window USED TO
 * reach back past the point where call history existed at all, so the nine
 * months before Feb 2026 contributed leads to every denominator and no calls
 * to any numerator. 90 days became the default for that reason, not because it
 * is the mock's default.
 *
 * THAT REASON EXPIRED ON 2026-09-06. The HubSpot meeting history was imported
 * without a company on the activity row, which is why it was invisible to every
 * company-grained query; `scripts/attach-meetings-to-companies.js --apply`
 * derived the company from the contact on 1,135 of those rows. Intro calls are
 * now continuous from 2024-07 at 5-25 a month, so a 12-month window no longer
 * has a call-less half. Whether 12 mo becomes the default is Gabriel's call —
 * the data no longer argues against it.
 */

export default function ReportsScope({ choices, value, custom, onChange, onCustom, busy }) {
  const [open, setOpen] = useState(false);
  const btn = useRef(null);
  const mobile = useIsMobile();
  const close = useCallback(() => setOpen(false), []);
  const today = todayYmd();

  return (
    <span className="crm-rep__scope">
      Scope
      <span className="crm-seg crm-seg--scope" role="group" aria-label="Report scope">
        {choices.map((choice) => (
          <button
            key={choice.days}
            type="button"
            className={!custom && choice.days === value ? "sel" : ""}
            aria-pressed={!custom && choice.days === value}
            disabled={busy}
            onClick={() => { setOpen(false); onChange(choice.days); }}
          >
            {choice.label}
          </button>
        ))}
        <button
          ref={btn}
          type="button"
          className={custom ? "sel" : ""}
          aria-pressed={!!custom}
          aria-haspopup="dialog"
          aria-expanded={open}
          disabled={busy}
          onClick={() => setOpen(!open)}
        >
          {/* When a range is in force the button shows it, because otherwise the
              only place the dates appear is the funnel caption further down. */}
          {custom ? rangeText(custom.from, custom.to) : "Custom"} ▾
        </button>
      </span>

      {open && (
        <Popover anchor={btn} onClose={close} align="right" label="Pick the report's dates">
          <RangePanel
            from={custom ? custom.from : null}
            to={custom ? custom.to : null}
            max={today}
            presets={rangePresets(["thisMonth", "lastMonth", "thisQuarter", "lastQuarter", "thisYear", "lastYear"], today)}
            note="Both dates included. Cohort conversion needs time to age, so a range ending recently will read as provisional."
            onApply={(r) => {
              close();
              onCustom(r);
            }}
            onCancel={close}
            mobile={mobile}
          />
        </Popover>
      )}
    </span>
  );
}
